import { useCallback, useContext, useEffect, useMemo } from "react";
import UserContext from "../services/UserContext";
import { RootState, useDispatch, useSelector } from "../store";
import {
  getSavedFilters,
  patchSavedFilters,
  SavedFilterPreset,
  SavedFiltersPage,
  SavedFiltersPayload,
} from "../slices/user-settings";
import { RequestStatus } from "../utils/Helpers/fetchStatus";

const newPresetId = (): string =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const nowIso = (): string => new Date().toISOString();

interface UseSavedFiltersResult<TFilters> {
  payload: SavedFiltersPayload<TFilters>;
  isLoading: boolean;
  isSaving: boolean;
  hasLoaded: boolean;
  save: (input: {
    name: string;
    filters: TFilters;
    setAsDefault: boolean;
  }) => Promise<void>;
  rename: (id: string, name: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
  setDefault: (id: string | null) => Promise<void>;
  isNameTaken: (name: string, excludeId?: string) => boolean;
}

const useSavedFilters = <TFilters>(
  page: SavedFiltersPage,
): UseSavedFiltersResult<TFilters> => {
  const dispatch = useDispatch();
  const context = useContext(UserContext);

  const { payload, fetchStatus, patchFetchStatus } = useSelector(
    (state: RootState) => state.userSettings.savedFilters[page],
  ) as {
    payload: SavedFiltersPayload<TFilters>;
    fetchStatus: string;
    patchFetchStatus: string;
  };

  useEffect(() => {
    if (RequestStatus.isNull(fetchStatus)) {
      dispatch(getSavedFilters({ context, page }));
    }
  }, [dispatch, context, page, fetchStatus]);

  const isNameTaken = useCallback(
    (name: string, excludeId?: string) => {
      const normalized = name.trim().toLowerCase();
      return payload.presets.some(
        (p) =>
          p.name.trim().toLowerCase() === normalized && p.id !== excludeId,
      );
    },
    [payload.presets],
  );

  const persist = useCallback(
    (next: SavedFiltersPayload<TFilters>) =>
      dispatch(patchSavedFilters({ context, page, payload: next })).unwrap(),
    [dispatch, context, page],
  );

  const save = useCallback(
    async (input: {
      name: string;
      filters: TFilters;
      setAsDefault: boolean;
    }): Promise<void> => {
      const preset: SavedFilterPreset<TFilters> = {
        id: newPresetId(),
        name: input.name.trim(),
        filters: input.filters,
        createdUtc: nowIso(),
        updatedUtc: nowIso(),
      };
      const next: SavedFiltersPayload<TFilters> = {
        version: 1,
        defaultId: input.setAsDefault ? preset.id : payload.defaultId,
        presets: [...payload.presets, preset],
      };
      await persist(next);
    },
    [payload.defaultId, payload.presets, persist],
  );

  const rename = useCallback(
    async (id: string, name: string) => {
      const next: SavedFiltersPayload<TFilters> = {
        version: 1,
        defaultId: payload.defaultId,
        presets: payload.presets.map((p) =>
          p.id === id ? { ...p, name: name.trim(), updatedUtc: nowIso() } : p,
        ),
      };
      await persist(next);
    },
    [payload.defaultId, payload.presets, persist],
  );

  const remove = useCallback(
    async (id: string) => {
      const next: SavedFiltersPayload<TFilters> = {
        version: 1,
        defaultId: payload.defaultId === id ? null : payload.defaultId,
        presets: payload.presets.filter((p) => p.id !== id),
      };
      await persist(next);
    },
    [payload.defaultId, payload.presets, persist],
  );

  const setDefault = useCallback(
    async (id: string | null) => {
      if (id && !payload.presets.some((p) => p.id === id)) return;
      const next: SavedFiltersPayload<TFilters> = {
        version: 1,
        defaultId: id,
        presets: payload.presets,
      };
      await persist(next);
    },
    [payload.presets, persist],
  );

  return useMemo(
    () => ({
      payload,
      isLoading: RequestStatus.isFetching(fetchStatus),
      isSaving: RequestStatus.isFetching(patchFetchStatus),
      hasLoaded:
        RequestStatus.isDone(fetchStatus) ||
        RequestStatus.isError(fetchStatus),
      save,
      rename,
      remove,
      setDefault,
      isNameTaken,
    }),
    [
      payload,
      fetchStatus,
      patchFetchStatus,
      save,
      rename,
      remove,
      setDefault,
      isNameTaken,
    ],
  );
};

export { useSavedFilters };
